/**
 * <split-lines> with hyphenation
 * ---------------------------------------------------------------------------------------------------------------------
 * Drop-in replacement for Impact's SplitLines (theme.js, "js/common/behavior/split-lines.js"), which powers the
 * "split fade / split clip / split rotation" heading animations (Theme settings → Animation → Heading apparition).
 *
 * WHY
 * Stock SplitLines wraps every LETTER in its own <span> to find where the lines break, then freezes each line into an
 * inline-block. With one element per letter the browser has no word to hyphenate, so a long German word is simply
 * chopped wherever the line runs out: "Überwachungskamer" / "as", no hyphen.
 *
 * HOW
 * The text is laid out once as a single text node, so the browser hyphenates it with the page's CSS (hyphens: auto
 * from wp-custom.css, language from <html lang>). Each character's position is then read with a Range, which finds
 * the lines without breaking the word up. Where a line ends between two letters the browser hyphenated there, so a
 * hyphen is added to that line before it is frozen.
 *
 * TIMING
 * Chrome loads its hyphenation dictionary asynchronously, and web fonts arrive late too, so the very first layout can
 * have neither. Lines frozen at that moment would keep the wrong breaks for good. So every heading is split once
 * straight away (the reveal animation needs lines to exist) and once more when fonts are loaded and hyphenation is
 * confirmed working, and again whenever a font finishes loading. If nothing changed, the existing line elements are
 * kept, so a running animation is not disturbed.
 *
 * A hyphen is only ever added after a probe has shown that this browser really hyphenates the page language. Without
 * a dictionary a mid-word break is a forced one at an arbitrary letter, and a hyphen there ("Türsprechanlag-en") would
 * look like bad hyphenation rather than none.
 *
 * COMPATIBILITY
 * theme.js only defines split-lines when nothing else has (customElements.get guard), and this file is loaded before
 * it with `defer`, so this definition wins without editing theme.js. The public surface is the same: `lines` returns
 * the shadow root's children, each `<span style="display:inline-block"><span style="display:block">…</span></span>`,
 * which is exactly what the reveal animations in theme.js read and animate.
 */
(() => {
  if (window.customElements.get('split-lines')) return;

  const isWordCharacter = (character) => /[\p{L}\p{N}]/u.test(character || '');

  // A long word in the page language, rendered 12ch wide with hyphens: auto and no other way to wrap. Unhyphenated it
  // is far wider than that; hyphenated, every syllable group fits. So it only fits once the browser can hyphenate the
  // language.
  const PROBE_WORDS = { de: 'Überwachungskameras', en: 'internationalization' };
  let hyphenationWorks = false;

  const hyphenationProbe = new Promise((resolve) => {
    const word = PROBE_WORDS[(document.documentElement.lang || '').slice(0, 2).toLowerCase()];
    if (!word) return resolve();

    const probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.textContent = word;
    probe.style.cssText =
      'position:absolute;top:0;left:-9999px;visibility:hidden;pointer-events:none;width:12ch;font-size:16px;' +
      'white-space:normal;overflow-wrap:normal;word-break:normal;-webkit-hyphens:auto;hyphens:auto;';
    document.body.appendChild(probe);

    const started = performance.now();
    const check = () => {
      if (probe.scrollWidth <= probe.clientWidth + 1) {
        hyphenationWorks = true;
      } else if (performance.now() - started < 2000) {
        return setTimeout(check, 50);
      }

      probe.remove();
      resolve();
    };

    check();
  });

  const layoutReady = Promise.race([
    Promise.all([document.fonts ? document.fonts.ready : Promise.resolve(), hyphenationProbe]),
    new Promise((resolve) => setTimeout(resolve, 3000))
  ]);

  // fonts.ready can resolve before a heading font has even started loading, so every heading is measured again
  // whenever a font finishes loading and once the page has fully loaded. Unchanged lines are left alone.
  const remeasure = new Set();
  const remeasureAll = () => remeasure.forEach((callback) => callback());
  document.fonts?.addEventListener('loadingdone', remeasureAll);
  window.addEventListener('load', remeasureAll);

  class HyphenatedSplitLines extends HTMLElement {
    #requireSplit = true;
    #lastScreenWidth = window.innerWidth;
    #resizeFrame = null;
    #signature = '';
    #remeasure = () => this.#split(true);

    constructor() {
      super();

      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(document.createElement('slot'));

      window.addEventListener('resize', () => {
        if (this.#resizeFrame) return;

        this.#resizeFrame = requestAnimationFrame(() => {
          this.#resizeFrame = null;

          if (this.#lastScreenWidth === window.innerWidth) return;

          this.#lastScreenWidth = window.innerWidth;
          this.#split(true);
        });
      });

      new MutationObserver(() => this.#split(true)).observe(this, { characterData: true, subtree: true });
    }

    connectedCallback() {
      this.#split();
      remeasure.add(this.#remeasure);
      layoutReady.then(() => this.isConnected && this.#split(true));
    }

    disconnectedCallback() {
      remeasure.delete(this.#remeasure);
    }

    get lines() {
      return Array.from(this.shadowRoot.children);
    }

    #split(force = false) {
      if (!this.#requireSplit && !force) return;

      const previousLines = Array.from(this.shadowRoot.childNodes);
      const text = this.textContent.replace(/\s/g, ' ');
      const textNode = document.createTextNode(text);
      this.shadowRoot.replaceChildren(textNode);

      // Group characters into lines by their rendered top edge. A space never starts a line: at a wrap it belongs to
      // the end of the previous one.
      //
      // The LAST client rect is used, not the bounding rect. Chrome reports the first letter after a hyphenation break
      // as two rects, the generated hyphen at the end of the previous line plus the letter itself, and their union
      // would put that letter on the previous line, shifting every hyphenated break one letter to the right.
      const range = document.createRange();
      const lines = [];
      let currentTop = null;

      for (let index = 0; index < text.length; index++) {
        range.setStart(textNode, index);
        range.setEnd(textNode, index + 1);

        const rects = range.getClientRects();
        const top = rects.length ? Math.round(rects[rects.length - 1].top) : currentTop;

        if (currentTop === null || (top !== currentTop && text[index] !== ' ')) {
          lines.push('');
          currentTop = top;
        }

        lines[lines.length - 1] += text[index];
      }

      if (hyphenationWorks) {
        for (let index = 0; index < lines.length - 1; index++) {
          if (isWordCharacter(lines[index].at(-1)) && isWordCharacter(lines[index + 1][0])) {
            lines[index] += '-';
          }
        }
      }

      // Same lines as already shown: put the existing elements back so a running reveal animation is not reset.
      const signature = lines.join('\n');
      if (signature === this.#signature && previousLines.length === lines.length) {
        this.shadowRoot.replaceChildren(...previousLines);
        return;
      }

      this.#signature = signature;
      this.shadowRoot.replaceChildren(
        ...lines.map((line) => {
          const outer = document.createElement('span');
          const inner = document.createElement('span');
          outer.style.display = 'inline-block';
          inner.style.display = 'block';
          inner.textContent = line;
          outer.appendChild(inner);
          return outer;
        })
      );

      this.#requireSplit = false;
    }
  }

  window.customElements.define('split-lines', HyphenatedSplitLines);
})();
